'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface PartnershipCTAProps {
  className?: string;
}

const PartnershipCTA = ({ className = '' }: PartnershipCTAProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-gradient-to-br from-primary to-brand-forest text-white ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-12 bg-white/20 rounded-lg mx-auto w-3/4"></div>
              <div className="h-6 bg-white/20 rounded-lg mx-auto w-1/2"></div>
              <div className="h-12 bg-white/20 rounded-lg mx-auto w-48 mt-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-gradient-to-br from-primary to-brand-forest text-white ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-headline text-4xl md:text-5xl font-semibold mb-6">
            Partner with Us for Measurable Impact
          </h2>
          <p className="font-body text-xl text-gray-200 leading-relaxed mb-12">
            Join institutional investors, government partners, and strategic collaborators in building sustainable value across Africa through systematic, transparent, and accountable development finance.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              href="/partnership-portal"
              className="w-full sm:w-auto px-8 py-4 bg-accent text-brand-charcoal font-body font-medium text-lg rounded-md hover:bg-accent/90 hover:shadow-institutional transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <span>Explore Partnership Opportunities</span>
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </Link>
            <button className="w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-body font-medium text-lg rounded-md hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-2 border border-white/30">
              <Icon name="DocumentArrowDownIcon" size={20} variant="outline" />
              <span>Download Impact Report</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="ChartBarIcon" size={32} variant="solid" className="text-accent mx-auto mb-4" />
              <h3 className="font-headline text-xl font-semibold mb-2">
                Quarterly Updates
              </h3>
              <p className="font-body text-sm text-gray-200">
                Regular impact reports with verified metrics and progress tracking
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="UserGroupIcon" size={32} variant="solid" className="text-accent mx-auto mb-4" />
              <h3 className="font-headline text-xl font-semibold mb-2">
                Stakeholder Access
              </h3>
              <p className="font-body text-sm text-gray-200">
                Direct engagement with leadership and project teams
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-accent mx-auto mb-4" />
              <h3 className="font-headline text-xl font-semibold mb-2">
                Verified Results
              </h3>
              <p className="font-body text-sm text-gray-200">
                Third-party audited outcomes and transparent reporting
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipCTA;